import { useRef, useEffect, RefObject } from "react";
import type { CoordinateSet } from "@/map/types/map";
import { POLYLINE_STYLE, POLYGON_STYLE } from "@/map/utils/mapConstants";

interface UseMapPathsProps {
  mapRef: RefObject<google.maps.Map | null>;
  mapsRef: RefObject<typeof google.maps | null>;
  basicCoordinates: CoordinateSet[];
  polygonCoordinates: CoordinateSet[];
  onTooltipShow?: (name: string, x: number, y: number, color?: string) => void;
  onTooltipHide?: () => void;
}

export const useMapPaths = ({
  mapRef,
  mapsRef,
  basicCoordinates,
  polygonCoordinates,
  onTooltipShow,
  onTooltipHide,
}: UseMapPathsProps) => {
  const polylineRefs = useRef<(google.maps.Polyline | google.maps.Polygon)[]>([]);

  /**
   * Create polygon with hover effects and tooltip
   */
  const createPolygon = (
    coordSet: CoordinateSet,
    fillOpacity: number,
    hoverFillOpacity: number
  ): google.maps.Polygon | null => {
    if (!mapRef.current || !mapsRef.current) return null;

    const polygon = new mapsRef.current.Polygon({
      paths: coordSet.path.map((point) => ({ lat: point.lat, lng: point.lng })),
      strokeColor: coordSet.color,
      strokeOpacity: POLYGON_STYLE.strokeOpacity,
      strokeWeight: POLYGON_STYLE.strokeWeight,
      fillColor: coordSet.color,
      fillOpacity,
      map: mapRef.current,
    });

    // Add hover effects
    polygon.addListener("mouseover", (event: google.maps.MapMouseEvent) => {
      polygon.setOptions({
        fillOpacity: hoverFillOpacity,
        strokeWeight: POLYGON_STYLE.hoverStrokeWeight,
      });

      // Show tooltip using screen coordinates from domEvent
      const domEvent = (event as any).domEvent as MouseEvent | undefined;
      if (domEvent) {
        onTooltipShow?.(coordSet.name, domEvent.clientX, domEvent.clientY, coordSet.color);
      }
    });

    polygon.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
      const domEvent = (event as any).domEvent as MouseEvent | undefined;
      if (domEvent) {
        onTooltipShow?.(coordSet.name, domEvent.clientX, domEvent.clientY, coordSet.color);
      }
    });

    polygon.addListener("mouseout", () => {
      polygon.setOptions({
        fillOpacity,
        strokeWeight: POLYGON_STYLE.strokeWeight,
      });
      onTooltipHide?.();
    });

    return polygon;
  };

  /**
   * Clear all existing paths
   */
  const clearPaths = () => {
    polylineRefs.current.forEach((polyline) => {
      polyline.setMap(null);
    });
    polylineRefs.current = [];
  };

  /**
   * Draw all paths on the map
   */
  useEffect(() => {
    if (!mapRef.current || !mapsRef.current) return;

    clearPaths();

    // Create polylines and polygons for basic coordinates
    basicCoordinates.forEach((coordSet) => {
      if (coordSet.path.length > 1 && !coordSet.disablePolyline) {
        // Create polyline (line) - only if not disabled
        const polyline = new mapsRef.current!.Polyline({
          path: coordSet.path.map((point) => ({ lat: point.lat, lng: point.lng })),
          geodesic: POLYLINE_STYLE.geodesic,
          strokeColor: coordSet.color,
          strokeOpacity: POLYLINE_STYLE.strokeOpacity,
          strokeWeight: POLYLINE_STYLE.strokeWeight,
          map: mapRef.current,
        });
        polylineRefs.current.push(polyline);

        // Create polygon (filled background)
        const polygon = createPolygon(
          coordSet,
          POLYGON_STYLE.basic.fillOpacity,
          POLYGON_STYLE.basic.hoverFillOpacity
        );
        if (polygon) {
          polylineRefs.current.push(polygon as any);
        }
      }
    });

    // Create polygons for polygon coordinates
    polygonCoordinates.forEach((coordSet) => {
      if (coordSet.path.length > 0) {
        const polygon = createPolygon(
          coordSet,
          POLYGON_STYLE.polygon.fillOpacity,
          POLYGON_STYLE.polygon.hoverFillOpacity
        );
        if (polygon) {
          polylineRefs.current.push(polygon as any);
        }
      }
    });

    return () => {
      clearPaths();
    };
  }, [basicCoordinates, polygonCoordinates, mapRef.current, mapsRef.current]);

  return {
    clearPaths,
  };
};
